"use client";

import { c as _c } from "react/compiler-runtime";
import { filterTree, getLeafs, type UITreeNode } from "@repo/design-system/components/filters/location-select";
import { checkIsSelected, handleToggleLocationSelect } from "@repo/design-system/components/filters/setters";
import { useLocationFilter } from "@repo/design-system/hooks/use-location-filter";
import { filterTypes } from "@repo/design-system/types/filter";
import type { LocationOptionTreeNode } from "@repo/design-system/types/filtermenu";
import { cn } from "@repo/design-system/utils/cn";
import { ChevronDown } from "lucide-react";
import type React from "react";
import { useMemo, useState } from "react";
import { Combobox, ComboboxContent, ComboboxControls, ComboboxEmpty, ComboboxFloatingLabel, ComboboxInput, ComboboxInputGroup, ComboboxItem, ComboboxList } from "~/components/ui/combobox-eon";
import { useLanguage } from "~/translations/loader/use-language";
import type { MultiSelectProps } from "./multi-select";
import { OptionDisplay } from "./select-layout-eon";
const LOCATION_FILTERS = [filterTypes.LOCATION_CITY_STATE, filterTypes.LOCATION_STATE, filterTypes.LOCATION_COUNTRY, filterTypes.LOCATION_CONTINENT];
interface FlatLocationItem {
  depth: number;
  hasChildren: boolean;
  treeNode: UITreeNode;
}
function collectExpanded(nodes: UITreeNode[], set: Set<string>) {
  for (const node of nodes) {
    if (node.isExpanded) {
      set.add(node.value);
    }
    if (node.children) {
      collectExpanded(node.children, set);
    }
  }
}
export function LocationSelect(t0) {
  const $ = _c(67);
  const {
    label,
    className
  } = t0;
  const t = useLanguage();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [expandedNodes, setExpandedNodes] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      filterTypes: LOCATION_FILTERS
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    activeFilters,
    options,
    clearFilter,
    setLocationFilters
  } = useLocationFilter(t2);
  const hasValue = activeFilters && activeFilters.length > 0;
  const floating = open;
  let t3;
  bb0: {
    if (!options.optionsTree) {
      let t4;
      if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = [];
        $[2] = t4;
      } else {
        t4 = $[2];
      }
      t3 = t4;
      break bb0;
    }
    if (!search) {
      t3 = options.optionsTree;
      break bb0;
    }
    let t4;
    if ($[3] !== options.optionsTree || $[4] !== search) {
      t4 = options.optionsTree?.map(node => filterTree(node, search)).filter(_temp);
      $[3] = options.optionsTree;
      $[4] = search;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    t3 = t4;
  }
  const filteredTree = t3;
  let effectiveExpanded;
  let t4;
  if ($[6] !== expandedNodes || $[7] !== filteredTree || $[8] !== search) {
    effectiveExpanded = new Set(expandedNodes);
    if (search) {
      collectExpanded(filteredTree, effectiveExpanded);
    }
    t4 = flattenVisibleTree(filteredTree, 0, effectiveExpanded);
    $[6] = expandedNodes;
    $[7] = filteredTree;
    $[8] = search;
    $[9] = effectiveExpanded;
    $[10] = t4;
  } else {
    effectiveExpanded = $[9];
    t4 = $[10];
  }
  const flatItems = t4;
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = value => {
      setExpandedNodes(prev => {
        const next = new Set(prev);
        if (next.has(value)) {
          next.delete(value);
        } else {
          next.add(value);
        }
        return next;
      });
    };
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const toggleExpanded = t5;
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = (e, hasChildren, isExpanded, value_0) => {
      if (!hasChildren) {
        return;
      }
      if (e.key === "ArrowRight" && !isExpanded || e.key === "ArrowLeft" && isExpanded) {
        e.preventDefault();
        toggleExpanded(value_0);
      }
    };
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleTreeKeyDown = t6;
  let t7;
  if ($[13] !== options.optionsTree) {
    t7 = options.optionsTree ?? [];
    $[13] = options.optionsTree;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const optionsTree = t7;
  let t8;
  if ($[15] !== activeFilters || $[16] !== optionsTree) {
    t8 = checkIsSelected(activeFilters, optionsTree);
    $[15] = activeFilters;
    $[16] = optionsTree;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const selectedValues = t8;
  let t9;
  if ($[18] !== flatItems || $[19] !== selectedValues) {
    let t10;
    if ($[21] !== selectedValues) {
      t10 = item => {
        const leafValues = getLeafs(item.treeNode).map(_temp2);
        return leafValues.some(value_1 => selectedValues.includes(value_1));
      };
      $[21] = selectedValues;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    t9 = flatItems.filter(t10);
    $[18] = flatItems;
    $[19] = selectedValues;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const selectedItems = t9;
  let t10;
  if ($[23] !== options || $[24] !== selectedItems || $[25] !== selectedValues || $[26] !== setLocationFilters) {
    t10 = async newSelection => {
      const currentSelectionValues = new Set(selectedItems.map(_temp3));
      const newSelectionValues = new Set(newSelection.map(_temp4));
      const changedItem = newSelection.find(item_3 => !currentSelectionValues.has(item_3.treeNode.value)) ?? selectedItems.find(item_2 => !newSelectionValues.has(item_2.treeNode.value));
      if (!changedItem) {
        return;
      }
      const leafValues_0 = getLeafs(changedItem.treeNode).map(_temp5);
      await handleToggleLocationSelect({
        isSelected: currentSelectionValues.has(changedItem.treeNode.value),
        selectedValues,
        leafValues: leafValues_0,
        options,
        onChange: setLocationFilters
      });
    };
    $[23] = options;
    $[24] = selectedItems;
    $[25] = selectedValues;
    $[26] = setLocationFilters;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const handleValueChange = t10;
  if (!options.optionsTree) {
    return null;
  }
  let t11;
  if ($[28] !== className) {
    t11 = cn("relative", className);
    $[28] = className;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== floating || $[31] !== label) {
    t12 = <ComboboxFloatingLabel floating={floating} htmlFor="location-select">{label}</ComboboxFloatingLabel>;
    $[30] = floating;
    $[31] = label;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const t13 = floating ? t.filterbar_filter1_placeholder_search : "";
  let t14;
  if ($[33] !== t13) {
    t14 = <ComboboxInput id="location-select" placeholder={t13} />;
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== activeFilters || $[36] !== hasValue) {
    t15 = hasValue && <OptionDisplay activeFilters={activeFilters} />;
    $[35] = activeFilters;
    $[36] = hasValue;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== clearFilter || $[39] !== hasValue || $[40] !== label || $[41] !== open) {
    t16 = <ComboboxControls hasValue={hasValue} label={label} onClear={clearFilter} open={open} />;
    $[38] = clearFilter;
    $[39] = hasValue;
    $[40] = label;
    $[41] = open;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== open || $[44] !== t14 || $[45] !== t15 || $[46] !== t16) {
    t17 = <ComboboxInputGroup open={open}>{t14}{t15}{t16}</ComboboxInputGroup>;
    $[43] = open;
    $[44] = t14;
    $[45] = t15;
    $[46] = t16;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== t.filtermenu_noOptions) {
    t18 = <ComboboxEmpty>{t.filtermenu_noOptions}</ComboboxEmpty>;
    $[48] = t.filtermenu_noOptions;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== effectiveExpanded || $[51] !== selectedValues) {
    t19 = <ComboboxList>{item_5 => {
        const {
          treeNode,
          depth,
          hasChildren: hasChildren_0
        } = item_5;
        const leafs = getLeafs(treeNode);
        const leafValues_1 = leafs.map(_temp8);
        const isSelected = leafValues_1.some(v => selectedValues.includes(v));
        const isExpanded_0 = effectiveExpanded.has(treeNode.value);
        return <ComboboxItem className={cn("text-sm")} key={treeNode.value} onKeyDown={e_0 => handleTreeKeyDown(e_0, hasChildren_0, isExpanded_0, treeNode.value)} style={{
          paddingLeft: `${0.75 + depth * 0.5}rem`
        }} value={item_5}><div className="flex items-center"><span>{treeNode.label}</span>{hasChildren_0 && <button className="ml-1 p-0" onClick={e_1 => {
              e_1.preventDefault();
              e_1.stopPropagation();
              toggleExpanded(treeNode.value);
            }} tabIndex={-1} type="button"><ChevronDown className={cn("size-4 text-foreground transition-transform", isSelected && "text-white", isExpanded_0 && "rotate-180")} /></button>}</div><span className="text-xs">{treeNode.count}</span></ComboboxItem>;
      }}</ComboboxList>;
    $[50] = effectiveExpanded;
    $[51] = selectedValues;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== t18 || $[54] !== t19) {
    t20 = <ComboboxContent>{t18}{t19}</ComboboxContent>;
    $[53] = t18;
    $[54] = t19;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== flatItems || $[57] !== handleValueChange || $[58] !== open || $[59] !== selectedItems || $[60] !== t17 || $[61] !== t20) {
    t21 = <Combobox filter={null} isItemEqualToValue={_temp6} items={flatItems} itemToStringLabel={_temp7} multiple={true} onInputValueChange={setSearch} onOpenChange={setOpen} onValueChange={handleValueChange} open={open} value={selectedItems}>{t17}{t20}</Combobox>;
    $[56] = flatItems;
    $[57] = handleValueChange;
    $[58] = open;
    $[59] = selectedItems;
    $[60] = t17;
    $[61] = t20;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  let t22;
  if ($[63] !== t11 || $[64] !== t12 || $[65] !== t21) {
    t22 = <div className={t11}>{t12}{t21}</div>;
    $[63] = t11;
    $[64] = t12;
    $[65] = t21;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  return t22;
}
function _temp8(n_0) {
  return n_0.value;
}
function _temp7(item_4) {
  return item_4.treeNode.label;
}
function _temp6(a, b) {
  return a.treeNode.value === b.treeNode.value;
}
function _temp5(node_1) {
  return node_1.value;
}
function _temp4(item_1) {
  return item_1.treeNode.value;
}
function _temp3(item_0) {
  return item_0.treeNode.value;
}
function _temp2(n) {
  return n.value;
}
function _temp(node_0) {
  return node_0 !== null;
}
function flattenVisibleTree(nodes: UITreeNode[], depth: number, expandedSet: Set<string>): FlatLocationItem[] {
  const result: FlatLocationItem[] = [];
  for (const node of nodes) {
    const hasChildren = !!node.children && node.children.length > 0 && node.children[0].label !== node.label;
    result.push({
      treeNode: node,
      depth,
      hasChildren
    });
    if (hasChildren && node.children && expandedSet.has(node.value)) {
      result.push(...flattenVisibleTree(node.children, depth + 1, expandedSet));
    }
  }
  return result;
}